import classNames from "classnames/dedupe";
import React, { ElementType, ReactNode, isValidElement, useMemo } from "react";

import { Link } from "../Link";
import { ILinkProps } from "../Link/Link";

export interface ILinkListProps {
  /**
   * List of Link elements
   * Rendered in form of 'ul' list
   */
  linksItems?: ILinkProps[];

  /** Custom title/header element for list
   *
   * @type 'ReactElement'
   */
  listTitle?: ReactNode;
  /**
   * Sets a custom html element for this component
   *
   * @default 'ul'
   */
  listComponent?: ElementType;

  classes?: {
    /** Styles applied to outer 'ul' element. */
    root?: string;
    /** Styles applied to each 'li' element. */
    listItem?: string;
    /** Styles applied to each 'Link' */
    link?: string;
    /** Styles applied to each 'Link' that is current route */
    activeLink?: string;
  };
  children?: ReactNode;
}

/** Render items of  'ILink' in form of li - list  */
function LinkList({
  listComponent: List = "ul",
  listTitle,
  linksItems,
  classes,
  children,
}: ILinkListProps) {
  const items = useMemo(
    () =>
      linksItems?.map((item, index) => ({
        ...item,
        itemKey: isValidElement(item.text) ? String(index) : item.text,
      })) ?? [],
    [],
  );

  return (
    <>
      {listTitle || null}
      <List className={classes?.root} data-testid="LinkList-component">
        {items.map(({ itemKey, ...item }) => (
          <li
            key={itemKey as string}
            className={classes?.listItem}
            data-testid="linklist-item"
          >
            <Link
              {...item}
              classes={{
                root: classNames(
                  classes?.link,
                  item.classes?.root,
                  item.active && classes?.activeLink,
                ),
              }}
            />
          </li>
        ))}
        {children}
      </List>
    </>
  );
}

export default LinkList;
